// Display · Tag
//
// Square-cornered semantic label chip. Visually distinct from `Pill`
// (RADIUS.pill = 999 — fully rounded) by using RADIUS.none (0 px) — true
// rectangular corners that clearly signal "data tag" rather than "status badge".
// Also distinct from Card (RADIUS.lg = 8 px) and soft buttons (RADIUS.xs = 4 px).
//
// Exports three shapes:
//   <Tag>         — soft fill chip with optional leading dot indicator.
//   <TagOutline>  — transparent background, border-only variant.
//   <TagBadge>    — circular/pill count badge (number or short string).
//
// Atom — no events, no configurable affordances. All visual values from
// `@ds/tokens/palette`.

import type { ReactNode } from "react";
import {
  CHIP_PAD,
  FONT_FAMILY,
  FONT_SIZE,
  FONT_WEIGHT,
  LETTER_SPACING,
  LINE_HEIGHT,
  RADIUS,
  TONE,
  type ToneName,
} from "@ds/tokens/palette";

// ─── Shared types ─────────────────────────────────────────────────────────────

export type TagTone = ToneName;
export type TagSize = "sm" | "md";

// ─── Tag ─────────────────────────────────────────────────────────────────────

export interface TagInput {
  /** The label content. Required. Accepts plain text or a small inline node (e.g. icon + text). */
  label: ReactNode;
  /**
   * Semantic tone (color family). Drives background, text, and border from TONE ramp.
   * Default: `"grey"`.
   */
  tone?: TagTone;
  /**
   * Size scale.
   * - `"sm"` — 11 px font, dense contexts (table cells).
   * - `"md"` (default) — 12 px font, standard contexts.
   */
  size?: TagSize;
  /**
   * Show a small filled dot before the label (same color as the label text).
   * Useful as a compact status signal inside a square chip.
   */
  dot?: boolean;
  /** `data-testid` on the root `<span>`. Atom — no inner testids auto-emitted. */
  testId?: string;
}

// Atom: no Output, no Actions — interfaces dropped per @ds Rule 3.

export type TagProps = TagInput;

export function Tag({
  label,
  tone = "grey",
  size = "md",
  dot,
  testId,
}: TagProps) {
  const t = TONE[tone];
  const isSmall = size === "sm";
  return (
    <span
      data-testid={testId}
      style={{
        display:       "inline-flex",
        alignItems:    "center",
        gap:           5, // off-scale 5px — between SPACE.xxs(2) and SPACE.xs(4); empirically tuned for chip dot+label rhythm
        background:    t.softBg,
        color:         t.text,
        border:        `1px solid ${t.border}`,
        borderRadius:  RADIUS.none,
        padding:       isSmall ? CHIP_PAD.sm : CHIP_PAD.md,
        fontFamily:    FONT_FAMILY.sans,
        fontSize:      isSmall ? FONT_SIZE.label : FONT_SIZE.small,
        fontWeight:    FONT_WEIGHT.medium,
        lineHeight:    LINE_HEIGHT.relaxed,
        letterSpacing: LETTER_SPACING.chip,
        whiteSpace:    "nowrap",
      }}
    >
      {dot && (
        <span
          style={{
            width:        isSmall ? 5 : 6,  // off-scale: between SPACE.xxs(2) and SPACE.xs(4)
            height:       isSmall ? 5 : 6,  // matches dot sizes in legacy Tag.tsx
            borderRadius: "50%",
            background:   t.text,
            flexShrink:   0,
          }}
        />
      )}
      {label}
    </span>
  );
}

// ─── TagOutline ───────────────────────────────────────────────────────────────

export interface TagOutlineInput {
  /** The label text. Required. */
  label: string;
  /**
   * Semantic tone. Default: `"grey"`.
   */
  tone?: TagTone;
  /** `data-testid` on the root `<span>`. Atom — no inner testids auto-emitted. */
  testId?: string;
}

export type TagOutlineProps = TagOutlineInput;

export function TagOutline({
  label,
  tone = "grey",
  testId,
}: TagOutlineProps) {
  const t = TONE[tone];
  return (
    <span
      data-testid={testId}
      style={{
        display:       "inline-flex",
        alignItems:    "center",
        background:    "transparent",
        color:         t.text,
        border:        `1px solid ${t.border}`,
        borderRadius:  RADIUS.none,
        padding:       "1px 7px", // intentionally tighter than CHIP_PAD.sm — border-only chips feel dense
        fontFamily:    FONT_FAMILY.sans,
        fontSize:      FONT_SIZE.small,
        fontWeight:    FONT_WEIGHT.medium,
        lineHeight:    LINE_HEIGHT.relaxed,
        letterSpacing: LETTER_SPACING.chip,
        whiteSpace:    "nowrap",
      }}
    >
      {label}
    </span>
  );
}

// ─── TagBadge ─────────────────────────────────────────────────────────────────

export interface TagBadgeInput {
  /** Count or short string to display. Required. */
  count: number | string;
  /**
   * Tone. Default: `"grey"`.
   */
  tone?: TagTone;
  /** `data-testid` on the root `<span>`. Atom — no inner testids auto-emitted. */
  testId?: string;
}

export type TagBadgeProps = TagBadgeInput;

/** Circular / pill count badge — distinct shape from Tag and TagOutline.
 *  Use for numeric counts alongside tags, e.g. filter result counts. */
export function TagBadge({
  count,
  tone = "grey",
  testId,
}: TagBadgeProps) {
  const t = TONE[tone];
  return (
    <span
      data-testid={testId}
      style={{
        display:        "inline-flex",
        alignItems:     "center",
        justifyContent: "center",
        minWidth:       18,  // off-scale: between SPACE.lg(16) and SPACE.xl(24); empirically tuned for 2-digit count
        height:         18,  // same off-scale value — circular badge
        background:     t.softBg,
        color:          t.text,
        border:         `1px solid ${t.border}`,
        borderRadius:   RADIUS.pill,
        fontFamily:     FONT_FAMILY.sans,
        fontSize:       FONT_SIZE.label,
        fontWeight:     FONT_WEIGHT.semibold,
        lineHeight:     LINE_HEIGHT.none,
        padding:        "0 5px", // off-scale: 5px horizontal inset for wide counts (3+ digits)
      }}
    >
      {count}
    </span>
  );
}
